/**
 * PATH: packages/ui/src/access/hubSubfeatures.ts
 * FILE_ID: UI-ACCESS-HUB-SUBFEATURES-001
 * PURPOSE: The ONE shared source of truth for the hub -> sub-feature grouping —
 *          which finer matrix rows live UNDER which parent navigation hub —
 *          plus the settings-flag lookup both consuming surfaces use.
 * WHY:     A company admin was confused that the org "Settings -> Modules"
 *          switches (whole-company on/off) don't line up 1:1 with the
 *          per-member access matrix (none/view/edit). Sub-features are grouped
 *          under a parent hub; stating that grouping ONCE keeps the Settings
 *          "Includes:" hint and the per-member matrix note consistent, and
 *          mirrors the backend SSOT (organisation/services/hub_access_write.py
 *          :: HUB_SUBFEATURES).
 * FLOW:
 *   hubKey -> subfeaturesForHub -> Subfeature[]   (matrix note grouping)
 *   enable_* flag -> subfeaturesForModuleFlag -> Subfeature[]  (Settings hint)
 * DEPENDENCIES: none (pure data + I->O helpers).
 * RELATED:
 *   - organisation/services/hub_access_write.py :: HUB_SUBFEATURES — backend SSOT
 *   - packages/ui/src/components/settings/ModuleAccessTab.tsx — "Includes:" hint
 *   - src/Pages/MainPages/TeamsHub/memberForm/MemberHubAccessSection.jsx — note
 */

export interface Subfeature {
  key: string
  label: string
}

/**
 * Parent navigation hub key -> the sub-feature matrix rows grouped under it.
 * VERIFIED against the code (URL prefixes + nav registry), NOT guessed:
 *   • workforce -> timesheets (/api/workforce/timesheets/), schedule + diary
 *     (FastAPI workforce-api; nav registry module 'workforce'; nav-entitlement
 *     rides `diary` on enable_workforce_hub).
 *   • email -> kanban (email_kanban app; nav item under Email), drafting (the
 *     Drafting Studio "was previously only reachable inside the Email Hub").
 *   • cfo -> governance (/api/compliance/ gate; governance matrix is a CFO
 *     surface, apps/cfo).
 * 'smartdocs' is intentionally NOT grouped — it is its own standalone hub app
 * (apps/smartdocs, own subdomain), not a sub-feature nested under a parent.
 * Mirrors backend HUB_SUBFEATURES; keep the two in lockstep.
 */
export const HUB_SUBFEATURES: Readonly<Record<string, readonly Subfeature[]>> = {
  workforce: [
    { key: 'timesheets', label: 'Timesheets' },
    { key: 'schedule', label: 'Schedule' },
    { key: 'diary', label: 'Diary' },
  ],
  email: [
    { key: 'kanban', label: 'Kanban' },
    { key: 'drafting', label: 'Drafting' },
  ],
  cfo: [{ key: 'governance', label: 'Governance' }],
}

/**
 * Settings "Modules" flag (OrganisationSettings.enable_*) -> the parent hub key
 * whose sub-features it groups. Only the modules that actually contain nested
 * sub-features appear here; every other toggle has no "Includes:" line.
 */
export const MODULE_FLAG_HUB: Readonly<Record<string, string>> = {
  enable_workforce_hub: 'workforce',
  enable_emails: 'email',
  enable_cfo_module: 'cfo',
}

/** f(hubKey) -> the sub-features grouped under that hub (fresh array, never the
 *  stored one; empty for a hub with no nested sub-features). Pure, total. */
export function subfeaturesForHub(hubKey: string | undefined): Subfeature[] {
  if (!hubKey) return []
  return [...(HUB_SUBFEATURES[hubKey] ?? [])]
}

/** f(enable_* flag) -> the sub-features that module groups (empty when the
 *  module has none). Pure, total. */
export function subfeaturesForModuleFlag(flag: string | undefined): Subfeature[] {
  if (!flag) return []
  return subfeaturesForHub(MODULE_FLAG_HUB[flag])
}

/**
 * Reverse of HUB_SUBFEATURES: sub-feature key -> its parent navigation hub key.
 * Built once from the grouping SSOT so a child can never be assigned to a
 * different parent than the one HUB_SUBFEATURES declares. e.g. 'timesheets' ->
 * 'workforce', 'governance' -> 'cfo', 'kanban' -> 'email'.
 */
export const SUBFEATURE_PARENT: Readonly<Record<string, string>> = Object.freeze(
  Object.entries(HUB_SUBFEATURES).reduce<Record<string, string>>(
    (acc, [parent, subs]) => {
      for (const sub of subs) acc[sub.key] = parent
      return acc
    },
    {},
  ),
)

/** f(childKey) -> the parent hub key it nests under, or undefined when the key
 *  is a top-level hub (or has no grouping). Pure, total. */
export function parentHubForSubfeature(key: string | undefined): string | undefined {
  if (!key) return undefined
  return SUBFEATURE_PARENT[key]
}

/**
 * The org-module key a matrix row is gated by. A sub-feature rides its PARENT
 * hub's module flag (workforce off ⇒ timesheets/schedule/diary off too), so we
 * resolve to the parent; a top-level hub gates on itself. Pure, total — mirrors
 * the backend, where a sub-feature is unreachable once its parent hub is off.
 */
export function moduleGateKey(hubKey: string): string {
  return SUBFEATURE_PARENT[hubKey] ?? hubKey
}
